import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import {
  Users,
  Crown,
  UserCheck,
  UserX,
  LogIn,
  Activity,
  TrendingUp,
  CalendarClock,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

interface UserTotals {
  total: number;
  premium: number;
  free: number;
  active: number;
  suspended: number;
  expiringSoon: number;
}

interface LoginStats {
  window_days: number;
  total_logins: number;
  logins_in_window: number;
  active_users_in_window: number;
  dau_today: number;
  wau: number;
  daily: Array<{ day: string; logins: number; users: number }>;
}

interface GrowthPoint {
  month: string;
  nuevos: number;
}

const WINDOW_OPTIONS = ['7', '30', '90'] as const;
type WindowOption = (typeof WINDOW_OPTIONS)[number];

export function UserStatsDashboard() {
  const [totals, setTotals] = useState<UserTotals | null>(null);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [loginStats, setLoginStats] = useState<LoginStats | null>(null);
  const [windowDays, setWindowDays] = useState<WindowOption>('30');
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfileStats = useCallback(async () => {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select(
        'created_at, subscription_tier, subscription_expires_at, is_active, suspended_until',
      );

    if (error) throw error;
    const rows = profiles || [];

    const now = Date.now();
    const in30Days = now + 30 * 24 * 60 * 60 * 1000;

    const t: UserTotals = {
      total: rows.length,
      premium: 0,
      free: 0,
      active: 0,
      suspended: 0,
      expiringSoon: 0,
    };

    const monthCounts = new Map<string, number>();

    for (const p of rows) {
      if (p.subscription_tier === 'premium') t.premium++;
      else t.free++;

      const suspendedActive = p.suspended_until
        ? new Date(p.suspended_until).getTime() > now
        : false;
      if (p.is_active && !suspendedActive) t.active++;
      if (suspendedActive || !p.is_active) t.suspended++;

      if (
        p.subscription_tier === 'premium' &&
        p.subscription_expires_at &&
        new Date(p.subscription_expires_at).getTime() <= in30Days &&
        new Date(p.subscription_expires_at).getTime() > now
      ) {
        t.expiringSoon++;
      }

      if (p.created_at) {
        const d = new Date(p.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthCounts.set(key, (monthCounts.get(key) || 0) + 1);
      }
    }

    setTotals(t);

    // Last 12 months of signups, chronological.
    const points: GrowthPoint[] = [];
    const cursor = new Date();
    cursor.setDate(1);
    for (let i = 11; i >= 0; i--) {
      const d = new Date(cursor.getFullYear(), cursor.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      points.push({
        month: d.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }),
        nuevos: monthCounts.get(key) || 0,
      });
    }
    setGrowth(points);
  }, []);

  const fetchLoginStats = useCallback(async (days: number) => {
    // record_login / admin_login_stats are new RPCs not yet in generated types.
    const { data, error } = await (supabase.rpc as unknown as (
      fn: string,
      args: Record<string, number>,
    ) => Promise<{ data: LoginStats | null; error: { message: string } | null }>)(
      'admin_login_stats',
      { p_days: days },
    );

    if (error) throw new Error(error.message);
    setLoginStats(data);
  }, []);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchProfileStats(), fetchLoginStats(parseInt(windowDays, 10))]);
    } catch (err) {
      console.error('Error loading user stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProfileStats, fetchLoginStats, windowDays]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const premiumRate =
    totals && totals.total > 0 ? (totals.premium / totals.total) * 100 : 0;

  const statCards = [
    {
      label: 'Total Usuarios',
      value: totals?.total ?? 0,
      icon: Users,
      color: 'bg-primary/10 text-primary',
    },
    {
      label: 'Premium',
      value: `${totals?.premium ?? 0} (${premiumRate.toFixed(0)}%)`,
      icon: Crown,
      color: 'bg-primary/10 text-primary',
    },
    {
      label: 'Free',
      value: totals?.free ?? 0,
      icon: Users,
      color: 'bg-muted text-muted-foreground',
    },
    {
      label: 'Activos',
      value: totals?.active ?? 0,
      icon: UserCheck,
      color: 'bg-success/10 text-success',
    },
    {
      label: 'Suspendidos/Baja',
      value: totals?.suspended ?? 0,
      icon: UserX,
      color: (totals?.suspended ?? 0) > 0 ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground',
    },
    {
      label: 'Premium por vencer (30d)',
      value: totals?.expiringSoon ?? 0,
      icon: CalendarClock,
      color: (totals?.expiringSoon ?? 0) > 0 ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground',
    },
  ];

  const loginCards = [
    {
      label: 'Activos hoy (DAU)',
      value: loginStats?.dau_today ?? 0,
      icon: Activity,
      color: 'bg-success/10 text-success',
    },
    {
      label: 'Activos 7 días (WAU)',
      value: loginStats?.wau ?? 0,
      icon: TrendingUp,
      color: 'bg-primary/10 text-primary',
    },
    {
      label: `Logins (${loginStats?.window_days ?? windowDays}d)`,
      value: loginStats?.logins_in_window ?? 0,
      icon: LogIn,
      color: 'bg-accent text-accent-foreground',
    },
    {
      label: `Usuarios únicos (${loginStats?.window_days ?? windowDays}d)`,
      value: loginStats?.active_users_in_window ?? 0,
      icon: UserCheck,
      color: 'bg-primary/10 text-primary',
    },
  ];

  const dailyChart = (loginStats?.daily ?? []).map((d) => ({
    label: new Date(d.day).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    logins: d.logins,
    users: d.users,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Estadísticas de Usuarios</h2>
          <p className="text-sm text-muted-foreground">
            Composición de la base y actividad de acceso
          </p>
        </div>
      </div>

      {/* User totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((c) => (
          <Card key={c.label}>
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col items-center text-center gap-2">
                <div className={`p-2 rounded-lg ${c.color}`}>
                  <c.icon className="h-5 w-5" />
                </div>
                <p className="text-2xl font-bold">{c.value}</p>
                <p className="text-xs text-muted-foreground">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Growth chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Crecimiento de usuarios (últimos 12 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={growth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="nuevos"
                stroke="hsl(var(--primary))"
                fill="url(#growthFill)"
                name="Nuevos usuarios"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Login activity */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Actividad de acceso</h3>
        <Select value={windowDays} onValueChange={(v) => setWindowDays(v as WindowOption)}>
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {loginCards.map((c) => (
          <Card key={c.label}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${c.color}`}>
                  <c.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{c.label}</p>
                  <p className="text-2xl font-bold">{c.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Logins por día</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyChart.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aún no hay registros de login en este periodo. Los datos empiezan a
              acumularse desde que se despliega el seguimiento.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dailyChart} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="logins" fill="hsl(var(--primary))" name="Logins" radius={[4, 4, 0, 0]} />
                <Bar dataKey="users" fill="hsl(var(--muted-foreground))" name="Usuarios únicos" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
